import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  User,
  Mail,
  Phone,
  Calendar,
  Edit2,
  Save,
  X,
  Camera,
  BookOpen,
  Award,
  Eye,
  EyeOff,
  Moon,
  Sun,
  Monitor,
  Globe,
  Lock,
  Trash2,
  ShieldCheck,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Upload
} from 'lucide-react'

interface UserProfile {
  id: string
  name: string
  email: string
  phone?: string
  phoneVerified?: boolean
  bio?: string
  avatar?: string
  dateOfBirth?: string
  location?: string
  joinedAt: string
  enrolledCourses: number
  completedCourses: number
  certificates: number
}

export default function UserProfile() {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [activeTab, setActiveTab] = useState<'profile' | 'preferences' | 'security'>('profile')

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+91',
    bio: '',
    dateOfBirth: '',
    location: ''
  })

  const [preferences, setPreferences] = useState({
    theme: localStorage.getItem('theme') || 'light'
  })

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token')

      const [profileResponse, enrollmentsResponse] = await Promise.all([
        fetch('/api/user/profile', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch('/api/enrollments', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ])
      if (!profileResponse.ok) {
        throw new Error('Failed to fetch profile')
      }

      const profileDataFromAPI = await profileResponse.json()
      const apiUser = profileDataFromAPI.user

      let enrolledCourses = 0
      let completedCourses = 0

      if (enrollmentsResponse.ok) {
        const enrollData = await enrollmentsResponse.json()
        enrolledCourses = enrollData.enrollments?.length || 0
        completedCourses = enrollData.enrollments?.filter((e: any) => e.completedAt).length || 0
      }

      const profileData: UserProfile = {
        id: apiUser._id || apiUser.id || '1',
        name: apiUser.name || 'Student',
        email: apiUser.email || '',
        phone: apiUser.phone || '',
        phoneVerified: apiUser.phoneVerified || false,
        bio: apiUser.bio || '',
        avatar: apiUser.avatar || '',
        dateOfBirth: apiUser.dateOfBirth || '',
        location: apiUser.location || '',
        joinedAt: apiUser.createdAt || new Date().toISOString(),
        enrolledCourses,
        completedCourses,
        certificates: completedCourses
      }

      let phoneOnly = apiUser.phone || ''
      let extractedCountryCode = apiUser.countryCode || '+91'

      const localUser = JSON.parse(localStorage.getItem('user') || '{}')
      localStorage.setItem('user', JSON.stringify({
        ...localUser,
        ...apiUser,
        userId: apiUser._id
      }))

      setProfile(profileData)
      setFormData({
        name: profileData.name,
        email: profileData.email,
        phone: phoneOnly,
        countryCode: extractedCountryCode,
        bio: profileData.bio || '',
        dateOfBirth: profileData.dateOfBirth ? new Date(profileData.dateOfBirth).toISOString().split('T')[0] : '',
        location: profileData.location || ''
      })
    } catch (error) {
      console.error('Error fetching profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB')
      return
    }

    setUploadingAvatar(true)
    const formDataUpload = new FormData()
    formDataUpload.append('avatar', file)

    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/user/avatar', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      })

      if (response.ok) {
        const data = await response.json()
        setProfile(prev => prev ? { ...prev, avatar: data.avatar } : null)

        const user = JSON.parse(localStorage.getItem('user') || '{}')
        localStorage.setItem('user', JSON.stringify({ ...user, avatar: data.avatar }))

        alert('Profile picture updated successfully!')
      } else {
        alert('Failed to upload image. Avatar upload may not be configured.')
      }
    } catch (error) {
      console.error('Error uploading image:', error)
      alert('Error uploading image')
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token')

      const dataToSend = {
        name: formData.name,
        countryCode: formData.countryCode,
        phone: formData.phone,
        bio: formData.bio,
        dateOfBirth: formData.dateOfBirth,
        location: formData.location
      }

      const response = await fetch('/api/user/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      })

      if (response.ok) {
        const data = await response.json()
        const user = JSON.parse(localStorage.getItem('user') || '{}')
        localStorage.setItem('user', JSON.stringify({
          ...user,
          name: formData.name,
          phone: data.user?.phone || formData.phone,
          countryCode: data.user?.countryCode || formData.countryCode,
          bio: formData.bio,
          dateOfBirth: formData.dateOfBirth,
          location: formData.location
        }))

        setIsEditing(false)
        fetchProfile()
        alert('Profile updated successfully!')
      } else {
        const errorData = await response.json()
        alert(errorData.message || 'Failed to update profile')
      }
    } catch (error) {
      console.error('Error updating profile:', error)
      alert('An error occurred while updating profile')
    }
  }

  const handleThemeChange = (theme: string) => {
    setPreferences(prev => ({ ...prev, theme }))
    localStorage.setItem('theme', theme)

    if (theme === 'dark') {
      document.documentElement.classList.add('dark')
    } else if (theme === 'light') {
      document.documentElement.classList.remove('dark')
    } else {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }
    }
  }

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match')
      return
    }

    if (passwordData.newPassword.length < 6) {
      alert('Password must be at least 6 characters')
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/user/change-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      })

      if (response.ok) {
        alert('Password changed successfully!')
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        })
      } else {
        const data = await response.json()
        alert(data.error || 'Failed to change password')
      }
    } catch (error) {
      console.error('Error changing password:', error)
      alert('An error occurred while changing password')
    }
  }

  const handleCancel = () => {
    fetchProfile()
    setIsEditing(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-violet-200 dark:border-violet-900 rounded-full animate-spin border-t-violet-600 dark:border-t-violet-400"></div>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
          <User className="w-8 h-8 text-slate-400" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">Profile Not Found</h2>
        <p className="text-slate-500 dark:text-slate-400 mt-2">We couldn't load your profile information.</p>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-12">
      {/* Hidden file input for avatar upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageUpload}
        accept="image/*"
        className="hidden"
      />

      {/* Header / Banner Section */}
      <div className="relative">
        {/* Banner with education-themed background image */}
        <div className="h-48 md:h-64 rounded-3xl relative overflow-hidden shadow-lg">
          {/* Background Image */}
          <img
            src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=1200&h=400&fit=crop&crop=bottom"
            alt="Education Banner"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600/80 via-purple-600/70 to-fuchsia-600/80"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>

          {/* Decorative Elements */}
          <div className="absolute top-4 right-4 flex gap-2">
            <div className="w-3 h-3 rounded-full bg-white/30 animate-pulse"></div>
            <div className="w-3 h-3 rounded-full bg-white/20 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-3 h-3 rounded-full bg-white/10 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          </div>

          {/* Academy Branding */}
          <div className="absolute top-6 left-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div className="text-white">
              <p className="font-bold text-lg">Sudha's Academy</p>
              <p className="text-xs text-white/70">Learning Platform</p>
            </div>
          </div>
        </div>

        {/* Profile Info Overlay */}
        <div className="px-6 md:px-10 relative -mt-20 flex flex-col md:flex-row items-end md:items-end gap-6">
          {/* Avatar with upload functionality */}
          <div className="relative group">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full p-1.5 bg-white dark:bg-[#0B0F19] shadow-2xl">
              <div
                className="w-full h-full rounded-full bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center overflow-hidden relative cursor-pointer"
                onClick={handleAvatarClick}
              >
                {uploadingAvatar ? (
                  <div className="w-8 h-8 border-4 border-violet-200 rounded-full animate-spin border-t-violet-600"></div>
                ) : profile.avatar ? (
                  <img src={profile.avatar} alt={profile.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-4xl md:text-5xl font-bold text-slate-400 dark:text-slate-500">
                    {profile.name.charAt(0).toUpperCase()}
                  </span>
                )}

                {/* Upload Overlay - Always visible on hover */}
                <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center cursor-pointer backdrop-blur-[2px] transition-opacity opacity-0 group-hover:opacity-100">
                  <Camera className="w-6 h-6 text-white mb-1" />
                  <span className="text-xs text-white font-medium">Change Photo</span>
                </div>
              </div>
            </div>
            {/* Online Status */}
            <div className="absolute bottom-4 right-4 w-5 h-5 bg-green-500 border-4 border-white dark:border-[#0B0F19] rounded-full"></div>
          </div>

          {/* Name & Bio */}
          <div className="flex-1 pb-2 text-center md:text-left">
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-1">{profile.name}</h1>
            <p className="text-slate-500 dark:text-slate-400 font-medium flex items-center justify-center md:justify-start gap-2">
              {profile.email}
              {profile.phoneVerified && (
                <ShieldCheck className="w-4 h-4 text-green-500" />
              )}
            </p>
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mt-4">
              <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 px-3 py-1.5 rounded-full shadow-sm border border-slate-200 dark:border-slate-700">
                <MapPin className="w-3.5 h-3.5 text-violet-500" />
                {profile.location || 'Location not set'}
              </div>
              <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 px-3 py-1.5 rounded-full shadow-sm border border-slate-200 dark:border-slate-700">
                <Calendar className="w-3.5 h-3.5 text-fuchsia-500" />
                Joined {new Date(profile.joinedAt).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pb-4 flex gap-3">
            {!isEditing && activeTab === 'profile' && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-semibold shadow-lg shadow-violet-500/30 transition-all flex items-center gap-2 transform hover:-translate-y-0.5"
              >
                <Edit2 className="w-4 h-4" />
                Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 px-4 md:px-0">

        {/* Left Sidebar / Navigation */}
        <div className="lg:col-span-3 space-y-6">
          {/* Navigation Tabs */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-2 shadow-sm border border-slate-200 dark:border-slate-700 sticky top-24">
            <nav className="space-y-1">
              {[
                { id: 'profile', label: 'Personal Info', icon: User },
                { id: 'preferences', label: 'Preferences', icon: Globe },
                { id: 'security', label: 'Security', icon: Lock },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id as any)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${activeTab === item.id
                    ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                    }`}
                >
                  <item.icon className={`w-5 h-5 ${activeTab === item.id ? 'text-violet-600 dark:text-violet-400' : 'text-slate-400'}`} />
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Quick Stats */}
          <div className="bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-2xl p-6 text-white shadow-lg">
            <h3 className="font-semibold text-white/90 mb-4">Your Progress</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/80">Courses Enrolled</span>
                <span className="font-bold text-xl">{profile.enrolledCourses}</span>
              </div>
              <div className="w-full bg-black/20 rounded-full h-1.5">
                <div className="bg-white h-1.5 rounded-full" style={{ width: `${Math.min(profile.enrolledCourses * 10, 100)}%` }}></div>
              </div>
              <div className="flex items-center justify-between pt-2">
                <span className="text-sm text-white/80">Certificates</span>
                <span className="font-bold text-xl">{profile.certificates}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Content Area */}
        <div className="lg:col-span-9">
          {activeTab === 'profile' && (
            <div className="space-y-6 animate-fadeIn">
              {/* About Me Card */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <User className="w-5 h-5 text-violet-500" />
                    About Me
                  </h3>
                </div>

                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Full Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all text-slate-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Bio</label>
                      <textarea
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all resize-none text-slate-900 dark:text-white"
                        placeholder="Tell us a bit about yourself..."
                      />
                    </div>
                  </div>
                ) : (
                  <div className="prose dark:prose-invert max-w-none">
                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                      {profile.bio || "No bio added yet. Click edit to tell us about yourself!"}
                    </p>
                  </div>
                )}
              </div>

              {/* Contact Info Card */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-violet-500" />
                  Contact Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Email */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Email Address</label>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                      <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 dark:text-violet-400">
                        <Mail className="w-4 h-4" />
                      </div>
                      <span className="text-slate-700 dark:text-slate-200 font-medium">{profile.email}</span>
                      <span className="ml-auto text-xs bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full">Primary</span>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Phone Number</label>
                    {isEditing ? (
                      <div className="flex gap-2">
                        <select
                          value={formData.countryCode}
                          onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                          className="w-24 px-3 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-slate-900 dark:text-white"
                        >
                          <option value="+91">+91</option>
                          <option value="+1">+1</option>
                          <option value="+44">+44</option>
                        </select>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="flex-1 px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-slate-900 dark:text-white"
                          placeholder="Phone number"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                        <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400">
                          <Phone className="w-4 h-4" />
                        </div>
                        <span className="text-slate-700 dark:text-slate-200 font-medium">
                          {formData.phone ? `${formData.countryCode} ${formData.phone}` : 'Not provided'}
                        </span>
                        {profile.phoneVerified && (
                          <ShieldCheck className="w-4 h-4 text-green-500 ml-auto" />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Location */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Location</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-slate-900 dark:text-white"
                        placeholder="City, Country"
                      />
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                          <MapPin className="w-4 h-4" />
                        </div>
                        <span className="text-slate-700 dark:text-slate-200 font-medium">{profile.location || 'Not set'}</span>
                      </div>
                    )}
                  </div>

                  {/* DOB */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Date of Birth</label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-slate-900 dark:text-white"
                      />
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                        <div className="w-8 h-8 rounded-lg bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-600 dark:text-pink-400">
                          <Calendar className="w-4 h-4" />
                        </div>
                        <span className="text-slate-700 dark:text-slate-200 font-medium">
                          {profile.dateOfBirth ? new Date(profile.dateOfBirth).toLocaleDateString() : 'Not set'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Edit Actions */}
                {isEditing && (
                  <div className="flex items-center gap-4 mt-8 pt-6 border-t border-slate-100 dark:border-slate-700">
                    <button
                      onClick={handleSave}
                      className="flex-1 bg-violet-600 hover:bg-violet-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg shadow-violet-500/20 transition-all flex items-center justify-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      Save Changes
                    </button>
                    <button
                      onClick={handleCancel}
                      className="flex-1 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 px-6 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-violet-500" />
                  Appearance
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { id: 'light', label: 'Light Mode', icon: Sun, desc: 'Clean & Bright' },
                    { id: 'dark', label: 'Dark Mode', icon: Moon, desc: 'Easy on eyes' },
                    { id: 'system', label: 'System', icon: Monitor, desc: 'Match device' },
                  ].map((theme) => (
                    <button
                      key={theme.id}
                      onClick={() => handleThemeChange(theme.id)}
                      className={`relative p-4 rounded-xl border-2 text-left transition-all ${preferences.theme === theme.id
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-violet-300 dark:hover:border-slate-600'
                        }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${preferences.theme === theme.id ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
                        }`}>
                        <theme.icon className="w-5 h-5" />
                      </div>
                      <div className="font-semibold text-slate-900 dark:text-white">{theme.label}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{theme.desc}</div>

                      {preferences.theme === theme.id && (
                        <div className="absolute top-4 right-4 text-violet-500">
                          <CheckCircle2 className="w-5 h-5" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6 animate-fadeIn">
              {/* Password Change */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-violet-500" />
                  Change Password
                </h3>

                <div className="space-y-4 max-w-lg">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Current Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-slate-900 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">New Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-slate-900 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Confirm New Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-slate-900 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      onClick={handlePasswordChange}
                      className="bg-violet-600 hover:bg-violet-700 text-white px-6 py-2.5 rounded-xl font-semibold shadow-lg shadow-violet-500/20 transition-all"
                    >
                      Update Password
                    </button>
                  </div>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="bg-red-50 dark:bg-red-900/10 rounded-2xl p-6 md:p-8 border border-red-100 dark:border-red-900/30">
                <h3 className="text-xl font-bold text-red-700 dark:text-red-400 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Danger Zone
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-6 text-sm">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
                <button
                  onClick={() => navigate('/user/delete-profile')}
                  className="bg-white dark:bg-red-950 border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 px-6 py-2.5 rounded-xl font-semibold hover:bg-red-50 dark:hover:bg-red-900/30 transition-all flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}